import {
	ChangeDetectorRef,
	Component,
	ElementRef,
	OnDestroy,
	OnInit,
	ViewChild,
} from '@angular/core';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

import { NgForm } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { AuthService } from './../../service/auth.service';
import { NzModalService } from 'ng-zorro-antd/modal';

type SbDisabilityState = 'ticketLookup' | 'authenticated' | 'completed';

@Component({
	selector: 'app-sb-disability',
	standalone: false,
	templateUrl: './sb-disability.component.html',
	styleUrl: './sb-disability.component.scss',
})
export class SbDisabilityComponent implements OnInit, OnDestroy {
	state: SbDisabilityState = 'ticketLookup';

	isSubmitting: boolean = false;
	formSubmitAttempted: boolean = false;

	ticketReference: string = '';
	verifiedTicket: any = null;

	assessmentForm = {
		firstName: '',
		lastName: '',
		idNumber: '',
		emailAddress: '',
		cellPhoneNumber: '',
		disabilityDisclosure: '',
		onsetType: '',
		dateFirstExperienced: '',
		impairmentCategory: '',
		knowsDiagnosis: '',
		diagnosisName: '',
		diagnosedByPractitioner: '',
		practitionerType: '',
		practitionerName: '',
		practitionerAddress: '',

		selectedImpairments: [] as string[],
		otherImpairment: '',
		affectsAbilityToWork: '',
		functionalDifficulties: {} as { [key: string]: string },
		safetyRisk: '',
		selectedSafetyConcerns: [] as string[],
		safetyConcernsDescription: '',

		selectedWorkplaceBarriers: [] as string[],
		otherWorkplaceBarrier: '',
		affectedJobTasks: '',

		accommodationHelpful: '',
		selectedAccommodationNeeds: [] as string[],
		otherAccommodationNeed: '',
		otherSpecificAccommodations: '',
		accommodationAssistanceNeeded: [] as string[],
		biggestSupportDifference: '',

		previousAccommodationUsed: '',
		selectedPreviousAccommodations: [] as string[],
		otherPreviousAccommodation: '',
		previousAccommodationDescription: '',

		currentlyReceivingTreatment: '',
		selectedTreatmentTypes: [] as string[],
		otherTreatmentType: '',
		conditionGeneralPattern: '',
		conditionFlarePattern: '',
		flareFrequency: '',

		hasMedicalEvidence: '',
		eea1File: null as File | null,
		medicalEvidenceFiles: [] as File[],

		consentGiven: '',
		signatureData: '',
		signatureDate: '',
	};

	impairmentOptions: string[] = [
		'Difficulty walking or climbing stairs',
		'Hearing loss or reduced hearing',
		'Sensory sensitivity',
		'Difficulty standing for long periods',
		'Speech or communication difficulty',
		'Seizures / blackout episodes',
		'Difficulty sitting for long periods',
		'Concentration / memory difficulty',
		'Fatigue / low stamina',
		'Difficulty using hands / fine motor tasks',
		'Learning / processing difficulty',
		'Pain that affects activity',
		'Difficulty lifting / carrying',
		'Anxiety / panic / emotional regulation difficulty',
		'Difficulty travelling to work',
		'Vision loss or reduced vision',
		'Depression / low mood / motivation difficulty',
	];

	difficultyOptions: string[] = [
		'No difficulty',
		'Mild difficulty',
		'Moderate difficulty',
		'Severe difficulty',
	];

	difficultyRows: string[] = [
		'Getting to and from work',
		'Entering or moving around the workplace',
		'Sitting for periods required by my job',
		'Standing for periods required by my job',
		'Walking around at work',
		'Climbing stairs',
		'Lifting / carrying',
		'Using a computer / keyboard / mouse',
		'Reading printed material',
		'Reading from a screen',
		'Hearing in meetings or conversations',
		'Speaking / communicating clearly',
		'Concentrating for sustained periods',
		'Remembering instructions / information',
		'Managing workload pressure',
		'Interacting with colleagues / customers',
		'Working shifts / long hours',
		'Travelling for work',
		'Attending training',
		'Performing core duties of my role',
	];

	safetyConcernOptions: string[] = [
		'Risk of falling',
		'Risk due to fatigue',
		'Risk during emergencies / evacuation',
		'Risk when using stairs',
		'Risk due to seizures / blackouts / dizziness',
		'Risk linked to stress / panic / overwhelm',
		'Risk when driving / operating vehicles',
		'Risk due to reduced vision',
		'On reflection - I do not think there is any safety risk',
		'Risk when using machinery / equipment',
		'Risk due to reduced hearing',
	];

	workplaceBarrierOptions: string[] = [
		'Stairs',
		'Manual handling / lifting',
		'Driving',
		'Distance from parking / entrance',
		'Computer screen use',
		'Workstation / desk setup',
		'Access to toilet facilities',
		'Telephone use',
		'Repetitive movements',
		'Lighting',
		'Meetings',
		'Reading small print',
		'Noise',
		'Fast work pace',
		'Training format',
		'Open plan office environment',
		'Tight deadlines',
		'Emergency evacuation measures',
		'Prolonged sitting',
		'Shift work',
		'None of the above',
		'Prolonged standing',
		'Travelling',
	];

	accommodationNeedOptions: string[] = [
		'Reserved or closer parking',
		'Hearing support device',
		'Reduced travel requirement',
		'Ground floor workspace',
		'Special keyboard or mouse',
		'Written instructions',
		'Lift access',
		'Alternative input device',
		'Extra time for training',
		'Ramp / easier physical access',
		'Better lighting / task light',
		'Information in larger print',
		'Accessible toilet facilities',
		'Assistive technology assessment',
		'Information in accessible format',
		'Adjustable desk',
		'Flexible working hours',
		'Sign language interpreter',
		'Ergonomic chair',
		'Later start / earlier finish',
		'Communication support in meetings',
		'Footrest',
		'Extra rest breaks',
		'Job coaching / mentoring',
		'Handrails / support rails',
		'Reduced standing time',
		'More frequent check-ins with manager',
		'Reduced walking distance between work areas',
		'Reduced walking demands',
		'Clearer work planning / prioritisation',
		'Change to workstation location',
		'Temporary adjustment of duties',
		'Quiet workspace',
		'Large computer screen',
		'Work-from-home / remote work',
		'Permission to use coping tools / aids',
		'Screen magnification software',
		'Hybrid work arrangement',
		'Emergency evacuation assistance',
		'Screen reader software',
		'Adjusted shift pattern',
		'Emergency procedure adapted for my needs',
		'Voice-to-text software',
		'Time off for medical treatment / rehabilitation',
		'Alternate seating / location for safety reasons',
		'Noise-cancelling headset',
		'Phased return to work',
		'Amplified telephone',
	];

	accommodationAssistanceOptions: string[] = [
		'I am not sure what accommodations would help',
		'I would like someone to discuss accommodations with me',
	];

	previousAccommodationOptions: string[] = [
		'Ergonomic setup',
		'Communication support',
		'Flexible hours',
		'Time off for treatment',
		'Remote work',
		'Phased return to work',
		'Reduced physical duties',
		'Quiet workspace',
		'Assistive technology',
	];

	treatmentTypeOptions: string[] = [
		'Medication',
		'Hearing support',
		'Rehabilitation programme',
		'Counselling / psychotherapy',
		'Vision support',
		'Regular IV treatment',
		'Occupational therapy',
		'Assistive device',
		'Regular dialysis',
		'Physiotherapy',
		'Surgery planned',
		'Specialist care',
	];

	flarePatternOptions: string[] = ['Yes', 'No', 'Unsure'];

	flareFrequencyOptions: string[] = [
		'Daily',
		'Weekly',
		'Monthly',
		'Occasionally',
		'Only during certain tasks',
		'Not sure',
	];
	onsetTypeOptions: string[] = [
		'Sudden onset',
		'Gradual onset',
		'Since birth / early childhood',
		'Result of injury / accident',
		'Developed over many years',
		'Unsure',
	];

	impairmentCategoryOptions: string[] = [
		'Physical / mobility',
		'Visual',
		'Hearing',
		'Speech / communication',
		'Intellectual / learning',
		'Mental health / psychosocial',
		'Neurological',
		'Chronic medical condition',
		'Respiratory',
		'Pain-related condition',
		'Multiple impairments / conditions',
		'Unsure',
		'Other',
	];

	knowsDiagnosisOptions: string[] = ['Yes', 'No', 'Prefer not to state'];

	diagnosedByPractitionerOptions: string[] = [
		'Yes',
		'No',
		'In process / still being investigated',
		'Not sure',
	];

	affectsAbilityToWorkOptions: string[] = [
		'Yes, a lot',
		'Yes, sometimes',
		'Yes, a little',
		'Not currently',
		'Unsure',
	];

	yesNoUnsureOptions: string[] = ['Yes', 'No', 'Unsure'];

	currentlyReceivingTreatmentOptions: string[] = [
		'Yes',
		'No',
		'Not currently, but I may need it again / in future',
		'Prefer not to say',
	];

	conditionPatternOptions: string[] = [
		'Stable',
		'Improving',
		'Fluctuating',
		'Worsening',
		'Unsure',
	];

	medicalEvidenceOptions: string[] = [
		'Yes, I can upload them now',
		'Yes, but I will send them later',
		'No',
		'Not sure what is needed',
	];

	consentOptions: string[] = [
		'Yes',
		'No',
		'Not sure, I would like to discuss it',
	];
	practitionerTypeOptions: string[] = [
		'General Practitioner',
		'Specialist Physician',
		'Psychiatrist',
		'Psychologist',
		'Occupational Therapist',
		'Audiologist',
		'Ophthalmologist',
		'Neurologist',
		'Orthopaedic Surgeon',
		'Physiotherapist',
		'Other',
	];

	private timerInterval: any;

	private applyTicketPrefill(ticketData: any): void {
		if (!ticketData) {
			return;
		}

		const member = ticketData.member || ticketData;
		const person = member.person || member;

		this.assessmentForm.firstName =
			person.name ||
			person.firstName ||
			member.name ||
			member.firstName ||
			'';

		this.assessmentForm.lastName =
			person.surname ||
			person.lastName ||
			member.surname ||
			member.lastName ||
			'';

		this.assessmentForm.idNumber = person.idNumber || member.idNumber || '';

		this.assessmentForm.emailAddress =
			member.emailAddress ||
			member.email ||
			ticketData.emailAddress ||
			'';

		this.assessmentForm.cellPhoneNumber =
			member.msisdn ||
			member.cellPhoneNumber ||
			member.contactNumber ||
			'';

		this.assessmentForm.signatureDate = new Date()
			.toISOString()
			.substring(0, 10);
	}

	constructor(
		private route: ActivatedRoute,
		private authService: AuthService,
		private modalService: NzModalService,
		private cdr: ChangeDetectorRef,
	) {}

	ngOnInit(): void {
		// this.route.queryParamMap.subscribe((params) => {
		// 	this.token = params.get('token') || '';
		// 	console.log('SB Disability token:', this.token);
		// });
	}

	ngOnDestroy(): void {
		// this.clearTimer();
	}

	async submitAssessment(form: NgForm): Promise<void> {
		this.formSubmitAttempted = true;

		if (form.invalid || !this.assessmentForm.signatureData) {
			return;
		}

		if (!this.verifiedTicket || !this.ticketReference) {
			this.modalService.error({
				nzTitle: 'Case Reference Required',
				nzContent:
					'Please verify the case reference before submitting.',
			});
			return;
		}

		this.isSubmitting = true;

		try {
			const pdfFile = await this.generateAssessmentPdf();

			const formData = new FormData();

			const payload = {
				ticketReference: this.ticketReference,
				formData: {
					...this.assessmentForm,
					...form.value,
				},
			};

			formData.append(
				'payload',
				new Blob([JSON.stringify(payload)], {
					type: 'application/json',
				}),
			);

			formData.append(
				'selfReport',
				pdfFile,
				'Self-Report - Disability Verification.pdf',
			);

			if (this.assessmentForm.eea1File) {
				formData.append(
					'eea1Form',
					this.assessmentForm.eea1File,
					this.assessmentForm.eea1File.name,
				);
			}

			this.assessmentForm.medicalEvidenceFiles.forEach((file) => {
				formData.append('additionalMedicals', file, file.name);
			});

			this.authService
				.doSubmitSbDisabilityAssessment(formData)
				.subscribe({
					next: (result: any) => {
						this.isSubmitting = false;

						if (result.success === false) {
							this.modalService.error({
								nzTitle: 'Submission Failed',
								nzContent:
									result.message ||
									'The assessment could not be submitted.',
							});
							return;
						}

						this.state = 'completed';
					},
					error: () => {
						this.isSubmitting = false;

						this.modalService.error({
							nzTitle: 'Submission Failed',
							nzContent: 'The assessment could not be submitted.',
						});
					},
				});
		} catch (error) {
			console.error('PDF generation failed:', error);

			this.isSubmitting = false;

			this.modalService.error({
				nzTitle: 'PDF Error',
				nzContent: 'Could not generate the assessment PDF.',
			});
		}
	}

	toggleArrayValue(
		arrayName:
			| 'selectedImpairments'
			| 'selectedSafetyConcerns'
			| 'selectedWorkplaceBarriers'
			| 'selectedAccommodationNeeds'
			| 'accommodationAssistanceNeeded'
			| 'selectedPreviousAccommodations'
			| 'selectedTreatmentTypes',
		value: string,
		checked: boolean,
	): void {
		const list = this.assessmentForm[arrayName] as string[];

		if (checked) {
			if (!list.includes(value)) {
				list.push(value);
			}
			return;
		}

		this.assessmentForm[arrayName] = list.filter(
			(item) => item !== value,
		) as any;
	}
	onSingleFileSelected(event: Event, fieldName: 'eea1File'): void {
		const input = event.target as HTMLInputElement;

		if (!input.files || input.files.length === 0) {
			this.assessmentForm[fieldName] = null;
			return;
		}

		this.assessmentForm[fieldName] = input.files[0];
	}

	onMultipleFilesSelected(
		event: Event,
		fieldName: 'medicalEvidenceFiles',
	): void {
		const input = event.target as HTMLInputElement;

		if (!input.files || input.files.length === 0) {
			this.assessmentForm[fieldName] = [];
			return;
		}

		this.assessmentForm[fieldName] = Array.from(input.files);
	}

	@ViewChild('assessmentPdfContent')
	assessmentPdfContent!: ElementRef<HTMLDivElement>;

	private async generateAssessmentPdf(): Promise<File> {
		const element = this.assessmentPdfContent.nativeElement;

		const canvas = await html2canvas(element, {
			scale: 2,
			useCORS: true,
			backgroundColor: '#ffffff',
		});

		const imgData = canvas.toDataURL('image/png');

		const pdf = new jsPDF('p', 'mm', 'a4');

		const pdfWidth = pdf.internal.pageSize.getWidth();
		const pdfHeight = pdf.internal.pageSize.getHeight();

		const imgWidth = pdfWidth;
		const imgHeight = (canvas.height * imgWidth) / canvas.width;

		let heightLeft = imgHeight;
		let position = 0;

		pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
		heightLeft -= pdfHeight;

		while (heightLeft > 0) {
			position = heightLeft - imgHeight;
			pdf.addPage();
			pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
			heightLeft -= pdfHeight;
		}

		const blob = pdf.output('blob');

		return new File([blob], 'disability-self-assessment.pdf', {
			type: 'application/pdf',
		});
	}

	@ViewChild('signatureCanvas')
	set signatureCanvas(canvas: ElementRef<HTMLCanvasElement> | undefined) {
		if (!canvas) return;
		this.signatureCanvasRef = canvas;
		setTimeout(() => {
			this.setupSignatureCanvas();
		});
	}

	private signatureCanvasRef?: ElementRef<HTMLCanvasElement>;
	private ctx?: CanvasRenderingContext2D;
	private isDrawing = false;

	private setupSignatureCanvas(): void {
		if (!this.signatureCanvasRef) return;
		const canvas = this.signatureCanvasRef.nativeElement;
		const rect = canvas.getBoundingClientRect();
		canvas.width = rect.width || 600;
		canvas.height = 180;
		const context = canvas.getContext('2d');
		if (!context) return;
		this.ctx = context;
		this.ctx.lineWidth = 2;
		this.ctx.lineCap = 'round';
		this.ctx.lineJoin = 'round';
		this.ctx.strokeStyle = '#000';
	}

	startDrawing(event: MouseEvent | TouchEvent): void {
		event.preventDefault();
		if (!this.ctx) this.setupSignatureCanvas();
		if (!this.ctx) return;
		const position = this.getCanvasPosition(event);
		this.isDrawing = true;
		this.ctx.beginPath();
		this.ctx.moveTo(position.x, position.y);
	}

	draw(event: MouseEvent | TouchEvent): void {
		if (!this.isDrawing || !this.ctx) return;
		event.preventDefault();
		const position = this.getCanvasPosition(event);
		this.ctx.lineTo(position.x, position.y);
		this.ctx.stroke();
		this.updateSignatureData();
	}

	stopDrawing(): void {
		if (!this.isDrawing) return;
		this.isDrawing = false;
		this.updateSignatureData();
	}

	clearSignature(): void {
		if (!this.signatureCanvasRef || !this.ctx) return;
		const canvas = this.signatureCanvasRef.nativeElement;
		this.ctx.clearRect(0, 0, canvas.width, canvas.height);
		this.assessmentForm.signatureData = '';
	}

	private updateSignatureData(): void {
		if (!this.signatureCanvasRef) return;
		const canvas = this.signatureCanvasRef.nativeElement;
		this.assessmentForm.signatureData = canvas.toDataURL('image/png');
	}

	private getCanvasPosition(event: MouseEvent | TouchEvent): {
		x: number;
		y: number;
	} {
		if (!this.signatureCanvasRef) return { x: 0, y: 0 };
		const canvas = this.signatureCanvasRef.nativeElement;
		const rect = canvas.getBoundingClientRect();
		let clientX: number, clientY: number;
		if (event instanceof TouchEvent) {
			clientX =
				event.touches[0]?.clientX || event.changedTouches[0].clientX;
			clientY =
				event.touches[0]?.clientY || event.changedTouches[0].clientY;
		} else {
			clientX = event.clientX;
			clientY = event.clientY;
		}
		return { x: clientX - rect.left, y: clientY - rect.top };
	}

	verifyTicketReference(): void {
		if (!this.ticketReference || !this.ticketReference.trim()) {
			this.modalService.error({
				nzTitle: 'Ticket Reference Required',
				nzContent: 'Please enter the case reference before continuing.',
			});
			return;
		}

		this.isSubmitting = true;

		this.authService
			.doVerifySbDisabilityTicketReference(this.ticketReference.trim())
			.subscribe({
				next: (result: any) => {
					this.isSubmitting = false;

					if (result.success === false) {
						this.modalService.error({
							nzTitle: 'Case Not Found',
							nzContent:
								result.message ||
								'No case exists for the reference provided.',
						});
						return;
					}

					this.verifiedTicket =
						result.data || result.ticket || result;

					this.applyTicketPrefill(this.verifiedTicket);

					this.state = 'authenticated';
					this.cdr.detectChanges();
				},
				error: () => {
					this.isSubmitting = false;

					this.modalService.error({
						nzTitle: 'Case Not Found',
						nzContent:
							'The case reference could not be verified. Please check the reference and try again.',
					});
				},
			});
	}
}
